import { resolveActiveAccountId } from "../api/index.js";
import { makeWhopRequest } from "../journey/client.js";

export type WhopApp = {
	id: string;
	name: string;
	route?: string | null;
	hosted_url?: string | null;
	secrets?: Record<string, string> | null;
	created_at?: number;
};

export type WhopAppBuild = {
	id: string;
	status: "draft" | "pending" | "approved" | "rejected";
	platform: string;
	checksum: string;
	is_production: boolean;
	created_at?: number;
};

type WhopFile = {
	id: string;
	upload_status: string;
	upload_url?: string | null;
	upload_headers?: Record<string, string> | null;
};

export async function createApp(input: {
	name: string;
	route: string;
	company_id?: string;
}): Promise<WhopApp> {
	const company_id = input.company_id ?? (await resolveActiveAccountId());
	return makeWhopRequest<WhopApp>("POST", "/apps", { ...input, company_id });
}

const DEV_TOKEN_TTL_MS = (3 * 60 - 1) * 60 * 1000;

/**
 * Mints a short-lived access token from the CLI's credential (POST
 * /access_tokens): with an API key it's a company token inheriting the key's
 * permissions; with OAuth login the user and scopes come from the token
 * itself (which also pins the expiry). `whop apps dev` injects it into the
 * dev server so server-side SDK calls work without manual env vars.
 */
export async function createDevAccessToken(): Promise<{
	token: string;
	expires_at?: string | number | null;
}> {
	const company_id = await resolveActiveAccountId();
	return makeWhopRequest("POST", "/access_tokens", {
		...(company_id ? { company_id } : {}),
		expires_at: new Date(Date.now() + DEV_TOKEN_TTL_MS).toISOString(),
	});
}

export async function listApps(): Promise<WhopApp[]> {
	const companyId = await resolveActiveAccountId();
	const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
	const response = await makeWhopRequest<{ data?: WhopApp[] }>(
		"GET",
		`/apps${query}`,
	);
	return response.data ?? [];
}

export async function getApp(appId: string): Promise<WhopApp> {
	return makeWhopRequest<WhopApp>("GET", `/apps/${appId}`);
}

export async function updateAppRoute(
	appId: string,
	route: string,
): Promise<WhopApp> {
	return makeWhopRequest<WhopApp>("PATCH", `/apps/${appId}`, { route });
}

export async function updateAppSecrets(
	appId: string,
	secrets: Record<string, string | null>,
): Promise<WhopApp> {
	return makeWhopRequest<WhopApp>("PATCH", `/apps/${appId}`, { secrets });
}

export type WhopAppLog = {
	app_id: string;
	app_build_id: string;
	request_id: string;
	created_at: string;
	source: string;
	level: string;
	message: string;
	stack?: string | null;
	request_method?: string | null;
	request_path?: string | null;
	response_status?: number | null;
	outcome?: string | null;
	cpu_time_ms?: number;
	wall_time_ms?: number;
	truncated?: boolean;
};

export type WhopAppLogsPage = {
	data: WhopAppLog[];
	page_info?: {
		end_cursor?: string | null;
		start_cursor?: string | null;
		has_next_page?: boolean;
		has_previous_page?: boolean;
	};
};

export async function listAppLogs(
	appId: string,
	params: Record<string, string | number | undefined>,
): Promise<WhopAppLogsPage> {
	const query = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== "") query.set(key, String(value));
	}
	const qs = query.size > 0 ? `?${query}` : "";
	return makeWhopRequest<WhopAppLogsPage>("GET", `/apps/${appId}/logs${qs}`);
}

export async function createAppBuild(input: {
	app_id: string;
	checksum: string;
	file_id: string;
}): Promise<WhopAppBuild> {
	return makeWhopRequest<WhopAppBuild>("POST", "/app_builds", {
		app_id: input.app_id,
		platform: "web",
		checksum: input.checksum,
		attachment: { id: input.file_id },
	});
}

export async function getAppBuild(buildId: string): Promise<WhopAppBuild> {
	return makeWhopRequest<WhopAppBuild>("GET", `/app_builds/${buildId}`);
}

export async function promoteAppBuild(buildId: string): Promise<WhopAppBuild> {
	return makeWhopRequest<WhopAppBuild>(
		"POST",
		`/app_builds/${buildId}/promote`,
		{},
	);
}

/**
 * Uploads a build archive: creates a file record, PUTs the bytes to the
 * presigned URL, and returns the file id. The archive is public so the hosting
 * worker can fetch it from a stable CDN URL. The file is still `pending` when
 * this returns — call waitForFileReady before attaching it to a build.
 */
export async function uploadBuildArchive(
	zip: Uint8Array,
	filename: string,
): Promise<string> {
	const file = await makeWhopRequest<WhopFile>("POST", "/files", {
		filename,
		visibility: "public",
	});

	if (!file.upload_url) {
		throw new Error("File creation did not return an upload URL.");
	}

	const body = new Uint8Array(zip).buffer as ArrayBuffer;
	const uploadResponse = await fetch(file.upload_url, {
		method: "PUT",
		headers: file.upload_headers ?? { "Content-Type": "application/zip" },
		body,
	});
	if (!uploadResponse.ok) {
		throw new Error(
			`Archive upload failed: ${uploadResponse.status} ${await uploadResponse.text()}`,
		);
	}

	return file.id;
}

/**
 * Polls the file until upload processing marks it `ready`. Direct-to-S3 uploads
 * complete asynchronously (the S3 event pipeline verifies the object and flips
 * the status), so a build can only attach the file once it is ready.
 */
export async function waitForFileReady(
	fileId: string,
	timeoutMs = 60_000,
): Promise<void> {
	const deadline = Date.now() + timeoutMs;
	while (Date.now() < deadline) {
		const file = await makeWhopRequest<WhopFile>("GET", `/files/${fileId}`);
		if (file.upload_status === "ready") return;
		await sleep(1_000);
	}
	throw new Error(
		"Timed out waiting for the build archive to finish processing.",
	);
}

export async function waitForBuildPromotion(
	buildId: string,
	timeoutMs = 30_000,
): Promise<WhopAppBuild> {
	const deadline = Date.now() + timeoutMs;
	let latest = await getAppBuild(buildId);
	while (Date.now() < deadline) {
		if (latest.is_production) return latest;
		if (latest.status === "rejected") {
			throw new Error("Build was rejected and cannot be promoted.");
		}
		await sleep(750);
		latest = await getAppBuild(buildId);
	}
	return latest;
}

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
